// Commentator ERA split for the center commentary list. SOURCE OF TRUTH: the
// sibling torah-map app's curated tree (torah-map/public/torah-map.json, nodes
// `tanakh_rishonim_on_tanakh` + `tanakh_acharonim_on_tanakh`) - the SAME nodes
// taxonomy.ts's ACCEPTED_COMMENTATORS was generated from. Each title is stored
// normalized, plus the "X" head of "X on Book"-style titles (mirroring the
// taxonomy's matching), so a Sefaria collectiveTitle matches either way.
//
// The section words are deliberately SOFT ("classic" / "later", not a hard
// rishon/acharon claim) because a few borderline names (Alshich, Sforno,
// Minchat Shai) sit in torah-map's rishonim node; "classic commentary" stays
// true for all of them. torah-map classifies NO approved commentator as
// modern (modern commentary is excluded by the taxonomy), so there is no
// "modern" tier here - anything unclassified honestly falls to `null` and the
// UI shows it under "More commentaries". This grouping only LABELS curated
// names - it never invents a ref or a relationship (accuracy-lock untouched).

import { norm } from "./taxonomy.ts";

export type CommentaryEra = "classic" | "later";

// torah-map tanakh_rishonim_on_tanakh children (titles + "on"-heads, normalized).
const CLASSIC = new Set<string>([
  "abarbanel", "alshich", "ateret zekeinim", "baal haturim", "bartenura", "bartenura on torah",
  "bekhor shor", "chizkuni", "daat zekenim", "eshkol hakofer", "eshkol hakofer on ruth",
  "ezra ben solomon", "ezra ben solomon on song of songs", "gevia kesef", "hadar zekenim",
  "hadar zekenim on torah", "ibn ezra", "immanuel of rome", "immanuel of rome on esther",
  "imrei daat", "imrei daat on proverbs", "joseph ibn yahya", "kitzur baal haturim",
  "mashmia yeshuah", "meiri", "meiri on psalms", "minchat shai", "paaneach raza",
  "rabbeinu bahya", "rabbeinu chananel", "radak", "ralbag", "ralbag beur hamilot", "ramban",
  "rashbam", "rashi", "riva", "riva on torah", "rosh", "rosh on torah", "saadia gaon",
  "second version of ibn ezra", "sforno", "shelom esther", "tikvat enosh",
  "tikvat enosh on job", "toledot yitzchak", "toledot yitzchak on torah", "tur haaroch",
  "tzror hamor", "tzror hamor on torah",
]);

// torah-map tanakh_acharonim_on_tanakh children (same generation).
const LATER = new Set<string>([
  "aderet eliyahu", "aderet eliyahu rabbi yosef chaim", "ahavat yehonatan", "alon bakhut",
  "alon bakhut on lamentations", "avi ezer", "baalei brit avram", "beit halevi",
  "beit halevi on torah", "chanukat hatorah", "chatam sofer", "chatam sofer on torah", "chida",
  "divrei david", "em lamikra", "eretz chemdah", "gur aryeh", "haamek davar",
  "haktav vehakabalah", "harchev davar", "iggeret shmuel", "iggeret shmuel on ruth",
  "imrei yosher", "keli chemdah", "keli chemdah on torah", "kli yakar", "lechem dimah",
  "lechem dimah on eikhah", "levush haorah", "malbim", "maskil ledavid", "mechokekei yehudah",
  "megalleh amukkot", "megalleh amukkot on torah", "mekhir yayin", "mekhir yayin on esther",
  "menot halevi", "menot halevi on esther", "meshech hochma", "metzudat david",
  "metzudat zion", "minchat ani", "minchat ani on torah", "minei targuma",
  "minei targuma on torah", "mizrachi", "nachalat yaakov", "netinah lager",
  "od yosef chai derashot", "ohel yaakov", "ohel yaakov on torah", "ohev ger", "ohr chadash",
  "or hachaim", "pardes yosef", "rav hirsch", "rav hirsch on torah", "reggio",
  "reggio on torah", "shadal", "siftei chakhamim", "siftei kohen", "siftei kohen on torah",
  "tevat gome", "tiferet yehonatan", "tiferet yehonatan on torah", "torah temimah",
  "tzafnat paneach", "tzafnat paneach on torah", "tzeenah ureenah", "yeriot shlomo",
  "yeriot shlomo on torah", "yesha elohim", "yesha elohim on esther",
]);

function lookup(name: string): CommentaryEra | null {
  if (CLASSIC.has(name)) return "classic";
  if (LATER.has(name)) return "later";
  return null;
}

// The era torah-map assigns this commentator, or null when it does not say.
// Matches on the commentator name first, then the "X" head of an "X on Book"
// ref (the same two keys commentaryAllowed() in taxonomy.ts checks).
export function commentaryEra(commentator: string, ref?: string): CommentaryEra | null {
  const byName = lookup(norm(commentator));
  if (byName) return byName;
  if (ref) {
    const head = norm(ref.split(/ on /i)[0]);
    if (head) return lookup(head);
  }
  return null;
}
